// UI config helpers. The backend now serves a *pre-filtered* UI config tailored
// to the current user (see /api/auth/me/capabilities). The frontend renders
// items as-is — there is no client-side permission filtering.

const sortUiItems = (items = []) =>
  [...items].sort((left, right) => (left?.sortOrder ?? 0) - (right?.sortOrder ?? 0));

export const getUiItemId = (item) => item?.id || item?._id || "";

export const resolveUiRoute = (item, isAdmin) => {
  if (!item) return "";
  if (isAdmin && item.adminRoute) return item.adminRoute;
  if (!isAdmin && item.employeeRoute) return item.employeeRoute;
  return item.route || "";
};

export const decorateUiItems = (items = [], { isAdmin = false } = {}) =>
  sortUiItems(items).map((item) => ({
    ...item,
    id: getUiItemId(item),
    children: Array.isArray(item.children)
      ? decorateUiItems(item.children, { isAdmin })
      : [],
    route: resolveUiRoute(item, isAdmin),
  }));

export const findUiItemById = (items = [], itemId) => {
  for (const item of items) {
    if (getUiItemId(item) === itemId) {
      return item;
    }
    if (Array.isArray(item?.children) && item.children.length > 0) {
      const match = findUiItemById(item.children, itemId);
      if (match) return match;
    }
  }
  return null;
};

/**
 * Decorated direct children of one sidebar item, by id. Hub/landing pages use
 * this to render their own contents — the nesting in `ui_configurations` is the
 * single source of truth for what belongs to which hub, so a page never has to
 * filter its siblings by a `group` string.
 */
export const getUiChildren = (config, itemId, { isAdmin = false } = {}) => {
  const item = findUiItemById(config?.sidebarItems || [], itemId);
  return decorateUiItems(item?.children || [], { isAdmin });
};

const ALL_AREA_KEYS = ["sidebarItems", "dashboardWidgets", "headerActions"];

const allAreaItems = (config) =>
  ALL_AREA_KEYS.flatMap((key) => (Array.isArray(config?.[key]) ? config[key] : []));

/**
 * Returns true if the given route-id is accessible (i.e. present in the
 * server-filtered UI config). Super Admin always returns true so we don't
 * accidentally hide tools the live config hasn't been migrated for yet.
 */
export const hasUiItemAccess = (config, itemId, subject) => {
  if (subject?.superAdmin) return true;
  if (!config || !itemId) return false;
  return findUiItemById(allAreaItems(config), itemId) !== null;
};
